// Hosts that serve large, user-controlled static assets (uploaded PDFs/images
// and the object store behind them). Sentry's browser tracing turns each
// request into a span, and a big user upload trips its "Large HTTP Payload"
// performance detector — noise we can't act on, since we control neither the
// CDN's delivery nor how large a file a user chooses to upload. Skip creating
// spans for these hosts (Sentry WEB-CLIENT-1F4).
//
// The uploads CDN is served from `uploads.agendahero.net` in production; the
// `.com` host is kept as a historical alias so the filter matches either — the
// earlier `.com`-only check never fired against the live `.net` URLs.
const UNTRACED_ASSET_HOSTS = [
  'digitaloceanspaces.com',
  'uploads.agendahero.net',
  'uploads.agendahero.com',
];

/**
 * Decide whether Sentry browser tracing should create a span for a request.
 * Returns `false` for the large user-uploaded asset hosts we don't want to
 * trace, and `true` for everything else (e.g. our own API).
 *
 * Matches on the request's normalized hostname (exact host or a subdomain of
 * it) rather than a raw substring, so a first-party URL that merely mentions an
 * asset host in its path/query still gets traced, and case variations in the
 * host don't slip past the filter.
 */
export const shouldTraceRequest = (url: string): boolean => {
  let hostname: string;
  try {
    ({ hostname } = new URL(url));
  } catch {
    // A URL we can't parse can't be attributed to an asset host — trace it.
    return true;
  }
  return !UNTRACED_ASSET_HOSTS.some(
    (host) => hostname === host || hostname.endsWith(`.${host}`)
  );
};
